import React, { useMemo } from "react";

/**
 * Props:
 * - onViewPastStreams: () => void
 * - onRetry?: () => void
 * - phase?: "LIVE" | "ENDED_GRACE" | "OFFLINE"
 * - status?: string
 * - title?: string
 * - thumbnail?: string
 * - graceRemainingMs?: number
 */
const OfflineNotice = ({
  onViewPastStreams,
  onRetry,
  phase,
  status: streamStatus,
  title,
  thumbnail,
  graceRemainingMs,
}) => {
  const isEndedGrace = phase === "ENDED_GRACE" || streamStatus === "ended";

  const timeLeft = useMemo(() => {
    const ms = Math.max(Number(graceRemainingMs ?? 0), 0);
    const totalSecs = Math.ceil(ms / 1000);
    const mm = String(Math.floor(totalSecs / 60)).padStart(2, "0");
    const ss = String(totalSecs % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [graceRemainingMs]);

  if (isEndedGrace) {
    // ---- Stream Ended (grace window) – light theme, mobile-friendly ----
    return (
      <section
        className="
          w-full max-w-lg
          rounded-2xl shadow-lg
          bg-gray-50
          px-4 py-5
          sm:px-8 sm:py-8
          md:px-10 md:py-8
          text-center
          mx-auto
          mt-4 sm:mt-8
        "
        aria-labelledby="ended-title"
      >
        {thumbnail ? (
          <div className="flex justify-center mb-4 sm:mb-5">
            <img
              src={thumbnail}
              alt=""
              className="w-28 h-28 sm:w-40 sm:h-40 md:w-44 md:h-44 rounded-xl object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : null}

        <h1
          id="ended-title"
          className="text-lg sm:text-2xl md:text-3xl font-semibold text-gray-800"
        >
          Live Stream ended
        </h1>

        <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">
          The live stream has ended. You can still watch previous sessions from
          our archive.
        </p>

        {/* {title ? (
          <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">
            {title}
          </p>
        ) : (
          <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">
            The live stream has ended. You can still watch previous sessions
            from our archive.
          </p>
        )} */}

        <div className="mt-4 sm:mt-6">
          <button
            type="button"
            onClick={onViewPastStreams}
            className="mt-2 px-5 py-2 text-sm sm:text-base font-medium text-white rounded-lg hover:bg-orange-600 transition w-full max-w-[15rem] sm:max-w-xs"
            style={{ backgroundColor: "#F47F23" }}
          >
            View Past Streams
          </button>

          <button
            type="button"
            onClick={() => window.history.back()}
            className="mt-3 px-5 py-2 text-sm sm:text-base font-medium border border-gray-400 text-gray-700 rounded-lg hover:bg-gray-200 transition w-full max-w-[15rem] sm:max-w-xs"
          >
            ← Go Back
          </button>
        </div>

        <p className="mt-3 text-gray-500 text-xs">
          If the stream resumes, we’ll auto-reconnect.
        </p>
      </section>
    );
  }

  // ---- Offline UI (unchanged) ----
  return (
    <section
      className="
        w-full max-w-lg
        rounded-2xl shadow-lg
        bg-gray-50
        px-4 py-5
        sm:px-8 sm:py-8
        md:px-10 md:py-10
        text-center
        mx-auto
        mt-4 sm:mt-8
      "
      aria-labelledby="offline-title"
    >
      <div className="flex justify-center mb-4 sm:mb-5">
        <img
          src="/images/logoImage.png"
          alt="Channel logo"
          className="w-16 h-auto sm:w-16 md:w-20 opacity-70"
          loading="lazy"
          decoding="async"
        />
      </div>

      <h1
        id="offline-title"
        className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800"
      >
        We’re currently offline
      </h1>

      <p className="mt-2 sm:mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">
        No live stream is active right now. Please check back later.
      </p>

      <div className="mt-4 sm:mt-6">
        <button
          style={{ backgroundColor: "#F47F23" }}
          className="mt-2 px-6 py-2 text-white rounded hover:bg-orange-600 transition w-full max-w-xs"
          onClick={onViewPastStreams}
        >
          View Past Streams
        </button>

        <button
          onClick={() => window.history.back()}
          className="mt-3 px-6 py-2 border border-gray-400 text-gray-700 rounded hover:bg-gray-200 transition w-full max-w-xs"
        >
          ← Go Back
        </button>
      </div>
    </section>
  );
};

export default OfflineNotice;
